import React, { useEffect, useState } from "react";
import {
  Button,
  CssBaseline,
  TextField,
  Link,
  Paper,
  Box,
  Grid,
  Typography,
  IconButton,
  Alert,
  CheckIcon,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import { makeStyles } from "@material-ui/core";
import Icon from "../../assets/img/icon.png";
import Banner from "../../assets/img/tagitstore_banner-01.jpg";
import authApi from "../../services/auth";
import { useDispatch } from "react-redux";
import { showLoader, hideLoader } from "../../redux/loader/actions";
import { showSnackbar } from "../../redux/snackbar/actions";
import Cookies from "js-cookie";
import GoogleIcon from "@mui/icons-material/Google";
import FacebookIcon from "@mui/icons-material/Facebook";
import AppleIcon from "@mui/icons-material/Apple";
import {
  setEncryptedCookie,
  getDecryptedCookie,
  removeCookie,
  encryptAES,
  decryptAES,
  removeNonBaseDomainCookies,
} from "../../utils";
import RememberDialog from "../signin/resendLink";

const useStyles = makeStyles((theme) => ({
  btn: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  bannerImage: {
    backgroundSize: "cover",
    backgroundPosition: "right",
    [theme.breakpoints.up("md")]: {
      backgroundImage: `url(${Banner})`,
      backgroundPosition: "left",
      backgroundSize: "cover",
    },
    [theme.breakpoints.down("sm")]: {
      display: "none !important",
    },
  },
  bg: {
    backgroundColor: "#f7f4f2",
  },
  socialIcons: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2),
  },
  socialIcon: {
    margin: theme.spacing(1),
    fontSize: "2rem",
  },
  googleIcon: {
    color: "#DB4437",
  },
  facebookIcon: {
    color: "#4267B2",
  },
  appleIcon: {
    color: "#000000",
  },
}));

function Copyright(props) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      {...props}
    >
      {"Copyright © "}
      <Link color="inherit" href="" sx={{ textDecoration: "none" }}>
        TAGITStore
      </Link>{" "}
      {new Date().getFullYear()}
    </Typography>
  );
}

const SignIn = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    removeCookie("email");
    removeCookie("password");
    removeCookie("id");
    removeCookie("abc");
    removeCookie("uuid");
    removeCookie("rt");
    removeCookie("at");
    removeCookie("fdf");
    removeCookie("def");
    removeNonBaseDomainCookies();
  }, []);

  const [remember, setRemember] = useState(false);
  const [rememberUUID, setRememberUUID] = useState();
  const [rememberMsg, setRememberMsg] = useState();

  let lastSubmitTime = 0;
  const [userInteracted, setUserInteracted] = useState(false);

  useEffect(() => {
    const handleUserInteraction = () => setUserInteracted(true);

    // document.addEventListener("mousemove", handleUserInteraction);
    document.addEventListener("keydown", handleUserInteraction);
    document.addEventListener("scroll", handleUserInteraction);
    document.addEventListener("touchstart", handleUserInteraction);

    return () => {
      // document.removeEventListener("mousemove", handleUserInteraction);
      document.removeEventListener("keydown", handleUserInteraction);
      document.removeEventListener("scroll", handleUserInteraction);
      document.removeEventListener("touchstart", handleUserInteraction);
    };
  }, []);

  const isBotDetected = () => {
    // Skip bot detection for mobile devices
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      return false;
    }

    // Check for common bot indicators only on desktop
    return (
      (navigator.webdriver && !/Android|iPhone/i.test(navigator.userAgent)) ||
      (!navigator.languages || navigator.languages.length === 0) ||
      (/bot|crawler|spider|headless|phantom/i.test(navigator.userAgent) &&
        !/Chrome|Firefox|Safari/i.test(navigator.userAgent)) ||
      /HeadlessChrome|puppeteer|phantomjs/i.test(navigator.userAgent) ||
      (!userInteracted && !/Android|iPhone/i.test(navigator.userAgent))
    );
  };

  const handleSubmit = (formData) => {
    const now = Date.now();

    if (isBotDetected()) {
      alert("Bot access is restricted!");
      window.location.reload();
      return;
    }

    if (now - lastSubmitTime < 3000) {
      alert("Multiple rapid submissions detected!");
      window.location.reload();
      return;
    }

    lastSubmitTime = now;

    if (formData?.honeypot) {
      alert("Suspicious activity detected!");
      return;
    }
    dispatch(showLoader());
    const data = {
      organization_name: formData?.organ?.trim() || "",
      username: formData?.name?.trim() || "",
      email: formData?.email?.trim().toLowerCase() || "",
      mobile_number: formData?.mobile_number?.trim() || "",
    };
    const encryptedData = encryptAES(data);
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.msg,
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );
        formik.resetForm();
        navigate("/");
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      if (!err?.response?.data?.regenerate) {
        dispatch(
          showSnackbar({
            message: err?.response?.data?.msg,
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "error",
          })
        );
      }
      setRemember(err?.response?.data?.regenerate);
      setRememberUUID(err?.response?.data?.uuid);
      setRememberMsg(err?.response?.data?.msg);
    };
    authApi.signUp(encryptedData).then(onSuccess, onFailure);
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      organ: "",
      email: "",
      mobile_number: "",
    },
    validationSchema: yup.object({
      name: yup
        .string()
        .required("Name is required")
        .min(2, "Name must be at least 2 characters")
        .max(50, "Name must be less than 50 characters")
        .matches(/^[a-zA-Z\s]*$/, "Name can only contain letters and spaces"),

      organ: yup
        .string()
        .required("Organization/Group is required")
        .min(2, "Organization/Group must be at least 2 characters")
        .max(50, "Organization/Group must be less than 50 characters")
        .matches(
          /^[a-zA-Z0-9 &@.,()/-]*$/,
          "Organization/Group can only contain letters, numbers, spaces, and the following special characters: &, -, @, ., ,, (), and /"
        ),

      email: yup
        .string()
        .matches(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          "Invalid email"
        )
        .required("Email required"),
      mobile_number: yup
        .string()
        .required("Mobile number is required")
        .matches(
          /^[0-9]{10,15}$/,
          "Mobile number must be between 10 and 15 digits"
        ),
    }),
    onSubmit: handleSubmit,
  });

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      formik.handleSubmit();
    }
  };

  return (
    <>
      <Grid container component="main" sx={{ height: "100vh" }}>
        <CssBaseline />
        <Grid
          item
          xs={false}
          sm={null}
          md={8}
          className={classes.bannerImage}
        />
        <Grid
          item
          xs={12}
          sm={12}
          md={4}
          pt={5}
          component={Paper}
          elevation={6}
          square
          className={classes.bg}
        >
          <Box
            sx={{
              mx: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box
              component="img"
              src={Icon}
              alt="logo"
              sx={{
                my: 2,
                width: "75px",
                height: "auto",
                maxWidth: "100%",
              }}
            />
            <Typography component="h1" variant="h5">
              Sign Up
            </Typography>
            <Box className={classes.socialIcons}></Box>
            <Box sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="name"
                label="Name"
                name="name"
                type="text"
                autoComplete="off"
                value={formik.values.name}
                onKeyDown={handleKeyDown}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="organ"
                onKeyDown={handleKeyDown}
                label="Organization/Group"
                name="organ"
                type="text"
                autoComplete="off"
                value={formik.values.organ}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.organ && Boolean(formik.errors.organ)}
                helperText={formik.touched.organ && formik.errors.organ}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email"
                onKeyDown={handleKeyDown}
                name="email"
                type={"text"}
                autoComplete="off"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
              <TextField
                margin="normal"
                fullWidth
                id="mobile_number"
                label="Mobile Number"
                name="mobile_number"
                type="text"
                autoComplete="off"
                onKeyDown={handleKeyDown}
                value={formik.values.mobile_number}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.mobile_number &&
                  Boolean(formik.errors.mobile_number)
                }
                helperText={
                  formik.touched.mobile_number && formik.errors.mobile_number
                }
              />
              <Button
                variant="contained"
                fullWidth
                sx={{
                  height: "100%",
                  background:
                    "linear-gradient(45deg, #ff4b2b 30%, #ff416c 90%)",
                  color: "#fff",
                  borderRadius: "8px",
                  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
                  textTransform: "uppercase",
                  fontWeight: "bold",
                  "&:hover": {
                    background:
                      "linear-gradient(45deg, #ff416c 30%, #ff4b2b 90%)",
                    boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.3)",
                  },
                }}
                onClick={formik.handleSubmit}
              >
                Sign Up
              </Button>
              <Grid container mt={1}>
                <Grid item xs={12} sm={6} md={6}>
                  <Link
                    href="/"
                    variant="body2"
                    sx={{ textDecoration: "none", color: "#9d9d9d" }}
                  >
                    Existing TAGITStore user? Log in
                  </Link>
                </Grid>
              </Grid>
              <Copyright sx={{ mt: 10 }} />
            </Box>
          </Box>
        </Grid>
      </Grid>
      <RememberDialog
        open={remember}
        onClose={() => setRemember(false)}
        message={rememberMsg}
        rememberUUID={rememberUUID}
        setRemember={setRemember}
        setRememberMsg={setRememberMsg}
        setRememberUUID={setRememberUUID}
      // onResend={handleResendLink}
      />
    </>
  );
};

export default SignIn;
